import React, { useState, useEffect } from "react";
import adminService from "../../api/services/adminService";

const BoostHistory = () => {
  const [boostHistory, setBoostHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [historyFilters, setHistoryFilters] = useState({
    user_id: "",
    action: "",
    start_date: "",
    end_date: "",
    search: "",
  });

  // Fetch boost history
  const fetchBoostHistory = async (page = 1, filters = {}) => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const params = {
        page: page,
        limit: 10,
        ...filters,
      };
      // Remove empty filter values
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null) {
          delete params[key];
        }
      });

      const response = await adminService.getBoostHistory(params);
      if (response.success && response.history) {
        setBoostHistory(response.history);
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        }
      }
    } catch (err) {
      console.error("Error fetching boost history:", err);
      setHistoryError(err?.message || "Failed to fetch boost history");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch history when filters/page changes
  useEffect(() => {
    fetchBoostHistory(currentPage, historyFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    historyFilters.user_id,
    historyFilters.action,
    historyFilters.start_date,
    historyFilters.end_date,
    historyFilters.search,
  ]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setHistoryFilters({
      ...historyFilters,
      [key]: value,
    });
    setCurrentPage(1);
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (historyFilters.user_id) count++;
    if (historyFilters.action) count++;
    if (historyFilters.start_date) count++;
    if (historyFilters.end_date) count++;
    if (historyFilters.search) count++;
    return count;
  };

  // Get filter display label
  const getFilterLabel = (key, value) => {
    if (key === "action") {
      const actionLabels = {
        purchased: "Purchased",
        extended: "Extended",
        cancelled: "Cancelled",
      };
      return actionLabels[value] || value;
    }
    if (key === "start_date" || key === "end_date") {
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return value;
  };

  // Remove a specific filter
  const removeFilter = (key) => {
    setHistoryFilters({
      ...historyFilters,
      [key]: "",
    });
    setCurrentPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get action badge color
  const getActionBadgeColor = (action) => {
    switch (action) {
      case "purchased":
        return "bg-green-100 text-green-800";
      case "extended":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="p-4 w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-md md:text-2xl font-bold text-gray-800">Boost History</h2>
        {getActiveFiltersCount() > 0 && (
          <button
            onClick={() => {
              setHistoryFilters({
                user_id: "",
                action: "",
                start_date: "",
                end_date: "",
                search: "",
              });
              setCurrentPage(1);
            }}
            className="text-sm text-red-600 hover:text-red-700 font-medium">
            Clear All Filters
          </button>
        )}
      </div>

      {/* Active Filters Display */}
      {getActiveFiltersCount() > 0 && (
        <div className="mb-4 flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <span className="text-xs md:text-sm font-medium text-gray-700 self-center">
            Active Filters:
          </span>
          {historyFilters.action && (
            <span className="inline-flex items-center   gap-1.5 px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium">
              Action: {getFilterLabel("action", historyFilters.action)}
              <button
                onClick={() => removeFilter("action")}
                className="hover:bg-red-200 rounded-full p-0.5 transition-colors">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          )}
          {historyFilters.user_id && (
            <span className="inline-flex items-center  gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
              User ID: {historyFilters.user_id}
              <button
                onClick={() => removeFilter("user_id")}
                className="hover:bg-purple-200 rounded-full p-0.5 transition-colors">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          )}
          {historyFilters.search && (
            <span className="inline-flex items-center text-xs md:text-sm gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
              Search: {historyFilters.search}
              <button
                onClick={() => removeFilter("search")}
                className="hover:bg-yellow-200 rounded-full p-0.5 transition-colors">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          )}
          {historyFilters.start_date && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              From: {getFilterLabel("start_date", historyFilters.start_date)}
              <button
                onClick={() => removeFilter("start_date")}
                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          )}
          {historyFilters.end_date && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              To: {getFilterLabel("end_date", historyFilters.end_date)}
              <button
                onClick={() => removeFilter("end_date")}
                className="hover:bg-green-200 rounded-full p-0.5 transition-colors">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid  grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
            Search (Name/Email/Phone)
          </label>
          <input
            type="text"
            value={historyFilters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Search users..."
            className="w-full text-xs md:text-sm p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
            User ID
          </label>
          <input
            type="number"
            value={historyFilters.user_id}
            onChange={(e) => handleFilterChange("user_id", e.target.value)}
            placeholder="Filter by user ID"
            className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
            Action
          </label>
          <select
            value={historyFilters.action}
            onChange={(e) => handleFilterChange("action", e.target.value)}
            className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700">
            <option value="">All Actions</option>
            <option value="purchased">Purchased</option>
            <option value="extended">Extended</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={historyFilters.start_date}
            onChange={(e) => handleFilterChange("start_date", e.target.value)}
            className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
            End Date
          </label>
          <input
            type="date"
            value={historyFilters.end_date}
            onChange={(e) => handleFilterChange("end_date", e.target.value)}
            className="w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
          />
        </div>
      </div>

      {/* Error Display */}
      {historyError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">{historyError}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loadingHistory && (
        <div className="text-center py-12">
          <div className="inline-flex items-center gap-3 text-red-600">
            <svg
              className="animate-spin h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="font-medium text-lg">
              Loading boost history...
            </span>
          </div>
        </div>
      )}

      {/* History Table */}
      {!loadingHistory && (
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y  divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Radius
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {boostHistory.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-12 text-center text-xs md:text-sm text-gray-500">
                      No boost history found
                    </td>
                  </tr>
                ) : (
                  boostHistory.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs md:text-sm font-medium text-gray-900">
                          {item.firstName} {item.lastName}
                        </div>
                        <div className="text-xs md:text-sm text-gray-500">
                          {item.email}
                        </div>
                        <div className="text-xs md:text-sm text-gray-400">
                          {item.contactNo}
                        </div>
                        <div className="text-xs md:text-sm text-gray-400">
                          ID: {item.user_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs md:text-sm font-medium ${getActionBadgeColor(
                            item.action
                          )}`}>
                          {getFilterLabel("action", item.action)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
                        {item.duration_minutes} min
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
                        {item.radius_km} km
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
                        {formatDate(item.start_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
                        {formatDate(item.end_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-500">
                        {item.payment_id || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-500">
                        {formatDate(item.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-xs md:text-sm font-medium text-gray-700">
                Showing page{" "}
                <span className="font-bold text-gray-900">{currentPage}</span>{" "}
                of <span className="font-bold text-gray-900">{totalPages}</span>{" "}
                (Total:{" "}
                <span className="font-bold text-red-600">{totalItems}</span>{" "}
                records)
              </div>
              <div className="flex gap-2 items-center justify-center">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Previous
                </button>
                <span className="text-xs md:text-sm text-gray-700">
                  <span className="font-bold">
                    {(currentPage - 1) * 10 + 1}
                  </span>{" "}
                  -{" "}
                  <span className="font-bold">
                    {Math.min(currentPage * 10, totalItems)}
                  </span>{" "}
                  of{" "}
                  <span className="font-bold text-red-600">{totalItems}</span>
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BoostHistory;
